import {getTestProps} from '@benchling/js-test-utils';
import {Accordion, Group, createStyles} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProgressToolCall, ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {
  SkillFileContentRenderer,
  MarkdownBlock,
  MonoBlock,
  ReadOnlyCodeBlock,
} from 'llm/chat-library/shared/SkillFileContentBlocks';
import {CouncilResponse, parseCouncilToolResponse} from 'llm/chat-library/tool-details/CouncilToolCallParser';
import CreateDashboardFromQueryButton, {
  canUserCreateOrEditInsightsDashboards,
} from 'llm/chat-library/tool-details/CreateDashboardFromQueryButton';
import {
  formatLoadSkillInput,
  isNativeLoadSkill,
  parseLoadSkillResponse,
} from 'llm/chat-library/tool-details/LoadSkillToolCallParser';
import {
  formatManageSkillInput,
  getManageSkillFiles,
  ParsedSkillResponse,
  parseManageSkillResponse,
  SkillFile,
} from 'llm/chat-library/tool-details/ManageSkillToolCallParser';
import {
  ParsedProgressToolCall,
  SearchNotebookEntriesToolCall,
  MCPToolCall,
  parseProgressToolCall,
} from 'llm/chat-library/tool-details/ParsedProgressToolCall';
import {ToolIconDisplay} from 'llm/chat-library/tool-details/ToolIconDisplay';
import {
  ToolIconSource,
  ToolType,
  getSleepDurationLabel,
  isMCPTool,
} from 'llm/chat-library/tool-details/types';
import {useToolDisplayName} from 'llm/chat-library/tool-details/useToolDisplayName';
import {useToolIcon} from 'llm/chat-library/tool-details/useToolIcon';
import {WebSearchResults} from 'llm/chat-library/tool-details/WebSearchResults';
import {parseWebSearchResults} from 'llm/chat-library/tool-details/WebSearchToolCallParser';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_PROXIMA_18,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import BenchChip, {isModelBenchChipSupported} from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARKER} from 'ui-library/Colors';
import {apiIdToIconName} from 'util/APIUtil';
import {assertNever} from 'util/Types';

// ============================================================================
// Skill file list rendering
// ============================================================================

const useSkillFileStyles = createStyles((theme) => ({
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
  },
  fileBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  },
  filePath: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: theme.colors.gray[7],
    fontFamily: theme.fontFamilyMonospace,
  },
}));

function FileContentBlock({file}: {file: SkillFile}): JSX.Element {
  const {classes} = useSkillFileStyles();
  return (
    <div className={classes.fileBlock}>
      <div className={classes.filePath}>{file.path}</div>
      <SkillFileContentRenderer virtualPath={file.path} content={file.content} />
    </div>
  );
}

function SkillFileList({parsed}: {parsed: ParsedSkillResponse}): JSX.Element {
  const {classes} = useSkillFileStyles();
  return (
    <div className={classes.fileList}>
      {parsed.files.map((file) => (
        <FileContentBlock key={file.path} file={file} />
      ))}
    </div>
  );
}

const useCouncilResponseStyles = createStyles((theme) => ({
  control: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: theme.colors.gray[7],
  },
  panel: {
    paddingTop: theme.spacing.xs,
  },
}));

interface DisplayedCouncilResponse extends CouncilResponse {
  label: string;
}

const COUNCIL_RESPONSE_WRAPPER_REGEX = /<council_response(?:\s|>)/;

function getDisplayedCouncilResponses(responses: Array<CouncilResponse>): Array<DisplayedCouncilResponse> {
  const providerCounts = new Map<string, number>();
  responses.forEach((response) => {
    providerCounts.set(response.provider, (providerCounts.get(response.provider) ?? 0) + 1);
  });

  const providerOccurrences = new Map<string, number>();
  return responses.map((response) => {
    const providerCount = providerCounts.get(response.provider) ?? 0;
    if (providerCount <= 1) {
      return {...response, label: response.provider};
    }

    const occurrence = (providerOccurrences.get(response.provider) ?? 0) + 1;
    providerOccurrences.set(response.provider, occurrence);
    return {...response, label: `${response.provider} (${occurrence})`};
  });
}

function getEmptyCouncilResponseText(rawResponse: string): string {
  if (COUNCIL_RESPONSE_WRAPPER_REGEX.test(rawResponse)) {
    return 'No response available';
  }
  return rawResponse || 'No response available';
}

function CouncilResponseList({responses}: {responses: Array<CouncilResponse>}): JSX.Element {
  const {classes} = useCouncilResponseStyles();
  const displayedResponses = getDisplayedCouncilResponses(responses);
  return (
    <Accordion
      multiple={true}
      defaultValue={[]}
      classNames={{control: classes.control, panel: classes.panel}}
    >
      {displayedResponses.map((response, index) => (
        <Accordion.Item key={`${response.label}-${index}`} value={`${response.label}-${index}`}>
          <Accordion.Control>{response.label}</Accordion.Control>
          <Accordion.Panel
            {...getTestProps({
              component: 'CouncilResponseList',
              element: 'responsePanel',
              key: response.label,
            })}
          >
            <MarkdownBlock markdown={response.content} />
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}

// ============================================================================
// Tool input/output formatting
// ============================================================================

function formatArgsForDisplay(args: Record<string, unknown>): string {
  return Object.entries(args)
    .map(([key, value]) => {
      if (typeof value === 'object' && value != null) {
        return `${key}: ${JSON.stringify(value, null, 2)}`;
      }
      return `${key}: ${value}`;
    })
    .join('\n');
}

function formatSearchNotebookEntriesInput(toolCall: SearchNotebookEntriesToolCall): string {
  const parts: Array<string> = [];

  const queries = Array.isArray(toolCall.queries) ? toolCall.queries : [];
  if (queries.length) {
    parts.push(`Queries: ${queries.map((q) => `"${q}"`).join(', ')}`);
  }

  const fields: Array<[string | undefined, string]> = [
    [toolCall.authoredBy?.join(', '), 'Authored By'],
    [toolCall.createdAfter, 'Created After'],
    [toolCall.createdBefore, 'Created Before'],
    [toolCall.modifiedAfter, 'Modified After'],
    [toolCall.modifiedBefore, 'Modified Before'],
    [toolCall.reviewCompletedAfter, 'Review Completed After'],
    [toolCall.reviewCompletedBefore, 'Review Completed Before'],
    [toolCall.reviewStatus, 'Review Status'],
    [toolCall.reviewRole, 'Review Role'],
    [toolCall.inProjectOrFolder, 'In Project/Folder'],
    [toolCall.inStudy, 'In Study'],
  ];

  for (const [value, label] of fields) {
    if (value) {
      parts.push(`${label}: ${value}`);
    }
  }

  if (toolCall.offset !== undefined && toolCall.offset > 0) {
    parts.push(`Offset: ${toolCall.offset}`);
  }

  return parts.length > 0 ? parts.join('\n') : 'No search parameters';
}

function isMCPToolCall(parsed: ParsedProgressToolCall): parsed is MCPToolCall {
  return isMCPTool(parsed.tool);
}

const useIdChipListStyles = createStyles((theme) => ({
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
  },
  // Stacks the chip list above the optional "Include Relevant Items" note for IdsToModelsTool.
  idsToModelsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  },
}));

/**
 * Render a list of Benchling API IDs as entity chips for the activity modal. Each ID self-fetches
 * its name/icon/link via BenchChip; IDs of unsupported types fall back to a plain icon+text chip so
 * nothing is dropped. Renders a fallback message when there are no IDs.
 */
function IdChipList({ids}: {ids: ReadonlyArray<string>}): JSX.Element {
  const {classes} = useIdChipListStyles();
  const validIds = ids.filter((id) => Boolean(id.trim()));
  if (validIds.length === 0) {
    return <MonoBlock text="No IDs provided" />;
  }
  return (
    <div className={classes.chipList}>
      {validIds.map((id) =>
        isModelBenchChipSupported(id) ? (
          <BenchChip key={id} size={ChipSize.MINI} id={id} />
        ) : (
          <Chip key={id} size={ChipSize.MINI} text={id} icon={apiIdToIconName(id)} />
        )
      )}
    </div>
  );
}

/**
 * Activity-modal input rendering for IdsToModelsTool: the looked-up objects as chips, plus the
 * optional "Include Relevant Items" flag when set.
 */
function IdsToModelsInputBlock({
  objectIds,
  includeRelevantItems,
}: {
  objectIds: ReadonlyArray<string>;
  includeRelevantItems?: boolean;
}): JSX.Element {
  const {classes} = useIdChipListStyles();
  return (
    <div className={classes.idsToModelsContainer}>
      <IdChipList ids={objectIds} />
      {includeRelevantItems ? <MonoBlock text="Include Relevant Items: true" /> : null}
    </div>
  );
}

/**
 * Returns structured input rendering for a tool, or null if the tool has no
 * structured input (in which case the caller should render a fallback).
 *
 * Also used by ToolCallItem in ChatProgress to determine whether a tool
 * has structured detail worth showing in the activity modal.
 */
export function getToolInputBlock(
  toolCall: ProgressToolCall,
  parsed: ParsedProgressToolCall | null
): JSX.Element | null {
  if (parsed == null) {
    return null;
  }

  if (isMCPToolCall(parsed)) {
    if (!parsed.args || Object.keys(parsed.args).length === 0) {
      return null;
    }
    return <MonoBlock text={formatArgsForDisplay(parsed.args)} />;
  }

  switch (parsed.tool) {
    case ToolType.SearchTool:
      return (
        <MonoBlock
          text={`Query: "${parsed.query || ''}"${parsed.objectTypes?.length ? `\nObject Types: ${parsed.objectTypes.join(', ')}` : ''}`}
        />
      );
    case ToolType.SearchNotebookEntries:
      return <MonoBlock text={formatSearchNotebookEntriesInput(parsed)} />;
    case ToolType.SearchSequencesTool:
    case ToolType.QueryWarehouseTool:
      return <ReadOnlyCodeBlock code={parsed.query || ''} language="pgsql" />;
    case ToolType.GetWarehouseOverviewTool:
      return fallbackInputBlock(toolCall);
    case ToolType.ReadBenchlingDocumentTool:
      return parsed.documentId ? <IdChipList ids={[parsed.documentId]} /> : <MonoBlock text="N/A" />;
    case ToolType.AttachmentTool:
      return <MonoBlock text={`Attachment ID: ${parsed.attachmentId || 'N/A'}`} />;
    case ToolType.PublicDataSubAgent:
    case ToolType.WebSearch:
      return <MonoBlock text={`Query: "${parsed.query || ''}"`} />;
    case ToolType.ResearchSubAgent:
      return <MonoBlock text={`Prompt: "${parsed.prompt || ''}"`} />;
    case ToolType.CouncilTool:
      return <MonoBlock text={`Prompt: "${parsed.prompt || ''}"`} />;
    case ToolType.IdsToModelsTool:
      return (
        <IdsToModelsInputBlock
          objectIds={parsed.objectIds ?? []}
          includeRelevantItems={parsed.includeRelevantItems}
        />
      );
    case ToolType.PlottingTool:
      return <ReadOnlyCodeBlock code={parsed.code || 'No code provided'} language="python" />;
    case ToolType.GenerateImageTool:
      return <MonoBlock text={`Prompt: "${parsed.prompt || ''}"`} />;
    case ToolType.CustomCodeTool:
    case ToolType.RunCodeTool:
      return <ReadOnlyCodeBlock code={parsed.code || 'No code provided'} language="python" />;
    case ToolType.LoadSkill:
      if (isNativeLoadSkill(parsed.skillId)) {
        return null;
      }
      return <MonoBlock text={formatLoadSkillInput(parsed)} />;
    case ToolType.ManageSkill:
      return <MonoBlock text={formatManageSkillInput(parsed)} />;

    case ToolType.ToolSearch:
      return <MonoBlock text={`Query: "${parsed.query || ''}"`} />;
    case ToolType.SleepTool:
      return <MonoBlock text={`Duration: ${getSleepDurationLabel(toolCall.args) ?? 'N/A'}`} />;

    case ToolType.CreateAnalysisPlan:
    case ToolType.CreateProcedureRunPlan:
    case ToolType.ValidateProcedureRunPlan:
    case ToolType.PromoteProcedureRunPlan:
      return null;

    // TODO: Add structured input rendering for Think in the activity modal.
    case ToolType.Think:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
      return null;
    default:
      assertNever(parsed);
      return null;
  }
}

/**
 * Returns structured output rendering for a tool, or null if the tool has no
 * structured output (in which case the caller should render a fallback).
 */
export function getToolOutputBlock(
  toolCall: ProgressToolCall,
  parsed: ParsedProgressToolCall | null
): JSX.Element | null {
  if (parsed == null) {
    return null;
  }

  if (isMCPToolCall(parsed)) {
    return null;
  }

  switch (parsed.tool) {
    case ToolType.QueryWarehouseTool:
      return <MarkdownBlock markdown={toolCall.responseText?.trim() || 'No response available'} />;
    case ToolType.GetWarehouseOverviewTool:
      return fallbackOutputBlock(toolCall);

    case ToolType.LoadSkill: {
      if (isNativeLoadSkill(parsed.skillId)) {
        return null;
      }
      const rawResponse = toolCall.responseText?.trim() || 'No skill content available';
      // responseText is XML-wrapped by the backend (see load_skill_tool.py).
      // Parse it into the unified skill-response shape so we can render using
      // the same SkillFileList pipeline as ManageSkill (SKILL.md frontmatter
      // is extracted into a structured name/description header by SkillMdBlock).
      const parsedResponse = parseLoadSkillResponse(rawResponse, parsed);
      if (!parsedResponse || !parsedResponse.success) {
        return <MonoBlock text={rawResponse} />;
      }
      return <SkillFileList parsed={parsedResponse} />;
    }

    case ToolType.ManageSkill: {
      const rawResponse = toolCall.responseText?.trim();
      const parsedResponse = rawResponse ? parseManageSkillResponse(rawResponse, parsed) : null;
      if (parsedResponse && !parsedResponse.success) {
        return <MonoBlock text={`Error: ${parsedResponse.message}`} />;
      }
      // Fall back to rendering just the input args when the response isn't
      // parseable (e.g. the tool is still pending or the backend wrapper is
      // missing) — users still see what the agent was trying to write.
      const renderable: ParsedSkillResponse = parsedResponse ?? {
        success: true,
        skillId: null,
        skillName: null,
        files: getManageSkillFiles(parsed),
        message: '',
      };
      if (renderable.files.length === 0) {
        return <MonoBlock text="No file contents available" />;
      }
      return <SkillFileList parsed={renderable} />;
    }

    case ToolType.RunCodeTool: {
      const raw = toolCall.responseText?.trim() || '';
      const hasChart = raw.includes('<bnch_chart>');
      const chartWarnings = [...raw.matchAll(/Warning: failed to save chart\(s\): [^\n]*/g)].map(
        (match) => match[0]
      );
      const cleaned = raw
        .replace(/Charts created \(embed these in your response using the tags below\):\n?/g, '')
        .replace(/<bnch_chart>[^<]*<\/bnch_chart>\n?/g, '')
        .replace(/Warning: failed to save chart\(s\): [^\n]*\n?/g, '')
        .trim();
      const output = [cleaned, ...chartWarnings]
        .filter((s) => s)
        .join('\n')
        .trim();
      if (!output && hasChart) {
        return <MonoBlock text="Chart created successfully" />;
      }
      if (!output) {
        return <MonoBlock text={raw || 'No response available'} />;
      }
      return <MonoBlock text={output} />;
    }

    case ToolType.WebSearch: {
      // The backend returns a JSON blob of search results; render them as
      // clickable source cards. Fall back to the raw text (e.g. a "no results"
      // or error message) when it isn't the expected JSON shape.
      const results = parseWebSearchResults(toolCall.responseText ?? '');
      if (results == null) {
        return null;
      }
      return <WebSearchResults results={results} />;
    }

    case ToolType.ToolSearch:
      // The output is an internal <functions> schema dump (ugly JSON, not user-facing). The loaded
      // tools are conveyed by the title, so the Output section is omitted entirely (see ToolDetail).
      return null;
    case ToolType.SleepTool:
      return null;

    case ToolType.CreateAnalysisPlan:
      return <MarkdownBlock markdown={toolCall.responseText?.trim() || 'No response available'} />;
    case ToolType.ResearchSubAgent:
      return <MarkdownBlock markdown={toolCall.responseText?.trim() || 'No response available'} />;
    case ToolType.CouncilTool: {
      const rawResponse = toolCall.responseText?.trim() || '';
      const responses = parseCouncilToolResponse(rawResponse);
      if (responses.length === 0) {
        return <MonoBlock text={getEmptyCouncilResponseText(rawResponse)} />;
      }
      return <CouncilResponseList responses={responses} />;
    }

    case ToolType.CreateProcedureRunPlan:
    case ToolType.ValidateProcedureRunPlan:
    case ToolType.PromoteProcedureRunPlan:
      return null;

    // TODO: Add structured output rendering for these displayed tools.
    case ToolType.SearchTool:
    case ToolType.ReadBenchlingDocumentTool:
    case ToolType.SearchNotebookEntries:
    case ToolType.SearchSequencesTool:
    case ToolType.AttachmentTool:
    case ToolType.PublicDataSubAgent:
    case ToolType.IdsToModelsTool:
    case ToolType.PlottingTool:
    case ToolType.GenerateImageTool:
    case ToolType.CustomCodeTool:
    case ToolType.Think:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
      return null;
    default:
      assertNever(parsed);
      return null;
  }
}

function fallbackInputBlock(toolCall: ProgressToolCall): JSX.Element {
  const args = toolCall.args ?? {};
  const text = Object.keys(args).length > 0 ? formatArgsForDisplay(args) : 'No input parameters';
  return <MonoBlock text={text} />;
}

function fallbackOutputBlock(toolCall: ProgressToolCall): JSX.Element {
  const text = toolCall.responseText?.trim();
  if (!text && toolCall.status !== ToolCallStatus.COMPLETED) {
    return <MonoBlock text="Waiting for response..." />;
  }
  return <MonoBlock text={text || 'No response available'} />;
}

// ============================================================================
// ToolDetail component
// ============================================================================

export interface ToolDetailProps {
  toolCall: ProgressToolCall;
}

function getToolOutputActions(parsed: ParsedProgressToolCall | null): JSX.Element | null {
  if (parsed == null || isMCPToolCall(parsed)) {
    return null;
  }
  if (
    parsed.tool === ToolType.QueryWarehouseTool &&
    parsed.query &&
    canUserCreateOrEditInsightsDashboards()
  ) {
    return <CreateDashboardFromQueryButton sqlQuery={parsed.query} />;
  }
  return null;
}

/**
 * Displays detailed information about a specific tool call.
 * Parses the generic ProgressToolCall into typed fields at this boundary
 * so that input/output formatting can use well-typed properties.
 */
export function ToolDetail({toolCall}: ToolDetailProps): JSX.Element {
  const getToolDisplayNameFormatted = useToolDisplayName();
  const getToolIconResolved = useToolIcon();
  const parsed = parseProgressToolCall(toolCall);
  const outputActions = getToolOutputActions(parsed);
  const toolIcon: ToolIconSource = getToolIconResolved(toolCall.tool);
  const hideLoadSkillDetail = parsed?.tool === ToolType.LoadSkill && isNativeLoadSkill(parsed.skillId);
  // ToolSearch's output is an internal <functions> schema dump; omit the Output section entirely
  // rather than showing the ugly JSON.
  const hideOutputSection = parsed?.tool === ToolType.ToolSearch;

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <ToolIconDisplay
          icon={toolIcon}
          glyphClassName={css(styles.toolIcon)}
          imgClassName={css(styles.toolIconImg)}
        />
        <div className={css(styles.toolName)}>
          {getToolDisplayNameFormatted(toolCall.tool, toolCall.args, toolCall.status)}
        </div>
      </div>

      {!hideLoadSkillDetail && (
        <div className={css(styles.content)}>
          <div className={css(styles.section)}>
            <div className={css(styles.sectionLabel)}>Input:</div>
            {getToolInputBlock(toolCall, parsed) ?? fallbackInputBlock(toolCall)}
          </div>
          {!hideOutputSection && (
            <div className={css(styles.section)}>
              <Group position="apart" spacing="md">
                <div className={css(styles.sectionLabel)}>Output:</div>
                {outputActions && <Group spacing="sm">{outputActions}</Group>}
              </Group>
              {getToolOutputBlock(toolCall, parsed) ?? fallbackOutputBlock(toolCall)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '4px',
  },

  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
  },

  toolIcon: {
    fontSize: '18px',
    color: GRAY_DARKER,
  },

  toolIconImg: {
    width: 18,
    height: 18,
    objectFit: 'contain' as const,
  },

  toolName: {
    ...FONT_PROXIMA_18,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  sectionLabel: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
  },
});
